import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { Locale } from "@/i18n";
import { localePath, PRODUCT_FAMILIES, ProductFamily, PRODUCT_LEAVES } from "@/lib/nav";
import { usePartnerModal } from "@/context/PartnerModalContext";
import { LEAF_DATA } from "@/pages/products/leafData";
const logoDark = "https://media.lacontessina.com/site/src/assets/logo-dark.png";
const logoWhite = "https://media.lacontessina.com/site/src/assets/logo-white.webp";
const classicImg = "https://media.lacontessina.com/site/src/assets/home/classic.webp";
const contemporaryImg = "https://media.lacontessina.com/site/src/assets/home/contemporary.webp";
const textileImg = "https://media.lacontessina.com/site/src/assets/home/textile.webp";
const FAMILY_PREVIEW: Record<ProductFamily, string> = {
  classic: classicImg,
  contemporary: contemporaryImg,
  textile: textileImg,
};

const LEAF_LABEL_KEY: Record<ProductFamily, Record<string, string>> = {
  classic: {
    furniture: "header.products.classic.furniture",
    kitchens: "header.products.classic.kitchens",
    wardrobe: "header.products.classic.wardrobes",
    office: "header.products.classic.offices",
    libraries: "header.products.classic.bookcases",
    boiserie: "header.products.classic.boiserie",
  },
  contemporary: {
    furniture: "header.products.contemporary.furniture",
    kitchens: "header.products.contemporary.kitchens",
    wardrobe: "header.products.contemporary.wardrobes",
    bathroom: "header.products.contemporary.bathroom",
  },
  textile: {
    curtains: "header.products.textile.curtains",
    bedspreads: "header.products.textile.bedspreads",
    tablecovers: "header.products.textile.tablecovers",
    cushions: "header.products.textile.cushions",
  },
};

const FAMILY_LABEL_KEY: Record<ProductFamily, string> = {
  classic: "style.classic",
  contemporary: "style.contemporary",
  textile: "style.textile",
};

export default function Header() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language as Locale) || "it";
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsHover, setProductsHover] = useState(false);
  const partner = usePartnerModal();
  const [previewFamily, setPreviewFamily] = useState<ProductFamily>("classic");
  const [previewLeaf, setPreviewLeaf] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const cancelClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setProductsHover(false), 140);
  };
  useEffect(() => () => cancelClose(), []);
  const previewLeafSrc =
    previewLeaf ? LEAF_DATA[previewFamily]?.[previewLeaf]?.gallery?.[0] : null;
  const previewLeafLabel =
    previewLeaf ? LEAF_LABEL_KEY[previewFamily]?.[previewLeaf] : null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isHome = /^\/[a-z]{2}\/?$/.test(location.pathname);
  const transparent = isHome && !scrolled;

  const items: Array<{ key: string; href: string }> = [
    { key: "header.menu.showroom", href: localePath(locale, "showroom") },
    { key: "header.menu.catalogs", href: localePath(locale, "catalogues") },
    { key: "header.menu.stock", href: localePath(locale, "stock") },
    { key: "header.menu.projects", href: localePath(locale, "projects") },
    { key: "header.menu.news", href: localePath(locale, "news") },
    { key: "header.menu.about", href: localePath(locale, "about") },
    { key: "header.menu.contact", href: localePath(locale, "contact") },
  ];

  const linkColor = transparent
    ? "text-white/75 hover:text-white"
    : "text-foreground/65 hover:text-foreground";
  const activeLinkColor = transparent ? "text-white" : "text-foreground";

  const homePath = localePath(locale, "home");
  const productsPath = localePath(locale, "products");
  const currentPath = location.pathname.replace(/\/$/, "") || "/";

  const isActive = (href: string) => {
    const target = href.replace(/\/$/, "") || "/";
    if (target === homePath.replace(/\/$/, "")) return currentPath === target;
    return currentPath === target || currentPath.startsWith(target + "/");
  };
  const isProductsActive = isActive(productsPath);

  return (
    <header
      className={
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500 " +
        (transparent
          ? "bg-transparent"
          : "bg-background/85 backdrop-blur-xl border-b border-foreground/8")
      }
    >
      <div className="lc-container-ext flex items-center justify-between h-20">
        <Link
          to={localePath(locale, "home")}
          className="flex items-center"
          aria-label="La Contessina"
        >
          <img
            src={transparent ? logoWhite : logoDark}
            alt="La Contessina"
            className="h-9 md:h-10 w-auto transition"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          <Link
            to={localePath(locale, "home")}
            className={`relative text-[13px] font-medium transition ${
              isActive(homePath) ? activeLinkColor : linkColor
            }`}
          >
            {t("header.menu.home")}
            {isActive(homePath) && (
              <span className="absolute left-0 right-0 -bottom-1.5 h-px bg-current" />
            )}
          </Link>
          {/* Products mega menu */}
          <div
            className="relative"
            onMouseEnter={() => {
              cancelClose();
              setProductsHover(true);
            }}
            onMouseLeave={scheduleClose}
          >
            <Link
              to={localePath(locale, "products")}
              className={`relative flex items-center gap-1 text-[13px] font-medium transition ${
                isProductsActive ? activeLinkColor : linkColor
              }`}
            >
              {t("header.menu.products")}
              <ChevronDown className="h-3.5 w-3.5" />
              {isProductsActive && (
                <span className="absolute left-0 right-4 -bottom-1.5 h-px bg-current" />
              )}
            </Link>
            {productsHover && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-5">
                <div className="bg-background border border-foreground/10 shadow-2xl rounded-[24px] py-7 pl-8 pr-6 grid grid-cols-[repeat(3,minmax(0,1fr))_240px] gap-8 min-w-[860px]">
                  {PRODUCT_FAMILIES.map((family) => (
                    <div
                      key={family}
                      onMouseEnter={() => {
                        setPreviewFamily(family);
                        setPreviewLeaf(null);
                      }}
                    >
                      <Link
                        to={localePath(locale, "products", family)}
                        className="block font-serif italic text-[22px] tracking-[-0.01em] text-foreground hover:text-bronze transition mb-4"
                      >
                        {t(FAMILY_LABEL_KEY[family])}
                      </Link>
                      <ul className="space-y-2.5">
                        {PRODUCT_LEAVES[family].map((leaf) => (
                          <li key={leaf}>
                            <Link
                              to={localePath(locale, "products", family, leaf)}
                              onMouseEnter={() => {
                                setPreviewFamily(family);
                                setPreviewLeaf(leaf);
                              }}
                              onMouseLeave={() => setPreviewLeaf(null)}
                              className="bronze-underline inline-block text-[13px] text-foreground/65 hover:text-foreground transition"
                            >
                              {t(LEAF_LABEL_KEY[family][leaf])}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <Link
                    to={
                      previewLeaf
                        ? localePath(locale, "products", previewFamily, previewLeaf)
                        : localePath(locale, "products", previewFamily)
                    }
                    className="relative block overflow-hidden rounded-[18px] bg-black/5 group"
                    aria-label={t(previewLeafLabel ?? FAMILY_LABEL_KEY[previewFamily])}
                  >
                    {PRODUCT_FAMILIES.map((f) => (
                      <img
                        key={f}
                        src={FAMILY_PREVIEW[f]}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:scale-[1.04] [transition:opacity_500ms_ease,transform_1200ms_ease]"
                        style={{ opacity: previewFamily === f ? 1 : 0 }}
                      />
                    ))}
                    {previewLeafSrc && (
                      <img
                        key={previewLeafSrc}
                        src={previewLeafSrc}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover opacity-100 transition-opacity duration-500 group-hover:scale-[1.04] [transition:opacity_500ms_ease,transform_1200ms_ease]"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/65" />
                    <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-white/70">
                          {t("header.menu.products")}
                        </div>
                        <div className="font-serif italic text-white text-[18px] mt-0.5">
                          {t(previewLeafLabel ?? FAMILY_LABEL_KEY[previewFamily])}
                        </div>
                      </div>
                      <span className="h-9 w-9 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white flex items-center justify-center transition group-hover:bg-white group-hover:text-foreground">
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {items.map((it) => (
            <Link
              key={it.key}
              to={it.href}
              className={`relative text-[13px] font-medium transition ${
                isActive(it.href) ? activeLinkColor : linkColor
              }`}
            >
              {t(it.key)}
              {isActive(it.href) && (
                <span className="absolute left-0 right-0 -bottom-1.5 h-px bg-current" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <LanguageSwitcher transparent={transparent} />
          </div>
          <button
            type="button"
            onClick={partner.open}
            className={
              "hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-medium transition-all " +
              (transparent
                ? "bg-white text-foreground hover:bg-white/90"
                : "bg-foreground text-background hover:bg-foreground/85")
            }
          >
            {t("button.become_partner")}
            <ArrowUpRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={"lg:hidden p-2 " + (transparent ? "text-white" : "text-foreground")}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-foreground/10 bg-background max-h-[85vh] overflow-y-auto">
          <div className="lc-container py-6 space-y-3">
            <Link
              to={localePath(locale, "home")}
              className={`relative block text-[14px] py-2 pl-3 ${
                isActive(homePath)
                  ? "font-semibold text-foreground border-l-2 border-foreground"
                  : "font-medium text-foreground/70 border-l-2 border-transparent"
              }`}
            >
              {t("header.menu.home")}
            </Link>
            <details open={isProductsActive}>
              <summary
                className={`cursor-pointer text-[14px] py-2 pl-3 border-l-2 ${
                  isProductsActive
                    ? "font-semibold text-foreground border-foreground"
                    : "font-medium text-foreground/70 border-transparent"
                }`}
              >
                {t("header.menu.products")}
              </summary>
              <div className="pl-3 mt-2 space-y-3">
                {PRODUCT_FAMILIES.map((family) => (
                  <div key={family}>
                    <Link
                      to={localePath(locale, "products", family)}
                      className={`block font-serif text-[16px] py-1 pl-3 border-l-2 ${
                        isActive(localePath(locale, "products", family))
                          ? "text-foreground border-foreground"
                          : "text-foreground/80 border-transparent"
                      }`}
                    >
                      {t(FAMILY_LABEL_KEY[family])}
                    </Link>
                    <ul className="pl-3 space-y-1">
                      {PRODUCT_LEAVES[family].map((leaf) => (
                        <li key={leaf}>
                          <Link
                            to={localePath(locale, "products", family, leaf)}
                            className={`text-[13px] py-1 block pl-3 border-l-2 ${
                              isActive(localePath(locale, "products", family, leaf))
                                ? "text-foreground font-medium border-foreground"
                                : "text-foreground/65 border-transparent"
                            }`}
                          >
                            {t(LEAF_LABEL_KEY[family][leaf])}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
            {items.map((it) => (
              <Link
                key={it.key}
                to={it.href}
                className={`block text-[14px] py-2 pl-3 border-l-2 ${
                  isActive(it.href)
                    ? "font-semibold text-foreground border-foreground"
                    : "font-medium text-foreground/70 border-transparent"
                }`}
              >
                {t(it.key)}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                partner.open();
              }}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background py-3 text-[13px] font-medium"
            >
              {t("button.become_partner")}
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <div className="pt-3 border-t border-foreground/10">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
